import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Container,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Modal,
  Tooltip,
  Loader,
  Flex,
  Pagination,
  Checkbox,
  MultiSelect,
  Switch
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle, IconPlus, IconSearch, IconTrash, IconEye } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import React from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { coreCompetencyService } from 'services/dacum/coreCompetency.service';
import { courseService } from 'services/lcms/course.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

interface SelectedCourse {
  courseId: string;
  courseName: string;
  courseSequence: number;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
  isRequired: number;
  positions: string[];
  learningObjectives?: string;
  learningHours?: number;
  modeOfDelivery?: string;
  businessUnits?: string[];
  liaIndicator?: boolean;
}

type CourseOption = {
  msLCMSCourseID: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
};

type CourseSelectionPayload = {
  courseId: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const formatCheckInfo = (name?: string, date?: string | null) => {
  if (!name && !date) return '-';
  const dateLabel = formatDateTime(date);
  if (!name) return dateLabel;
  if (dateLabel === '-') return name;
  return `${name} • ${dateLabel}`;
};

const decodeHtml = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

// Category descriptions mapping
const CATEGORY_DESCRIPTIONS = {
  'Fundamental Thinking Skill':
    'The ability to explain essential information and apply it accurately to perform tasks and solve routine problems.\n\n',
  'Higher Order Thinking':
    'The ability to analyze situations, make sound judgments, and create effective solutions in complex or unfamiliar contexts.\n\n'
};

const EditCoreCompetencyPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const competencyId = params.id || '';
  const businessUnitId = searchParams.get('businessunitid') || '';
  const jobFamilyId = searchParams.get('jobfamilyid') || '';
  const msSchoolCompetencyManagementID = searchParams.get('msSchoolCompetencyManagementID');

  // Get LPD job positions if passed
  const lpdJobPositionsParam = searchParams.get('lpdJobPositions');
  const lpdJobPositions: { id: string; name: string }[] = React.useMemo(() => {
    if (lpdJobPositionsParam) {
      try {
        return JSON.parse(lpdJobPositionsParam);
      } catch {
        return [];
      }
    }
    return [];
  }, [lpdJobPositionsParam]);

  const [courseModalOpened, { open: openCourseModal, close: closeCourseModal }] =
    useDisclosure(false);
  const [selectedCourses, setSelectedCourses] = React.useState<SelectedCourse[]>([]);
  const [competencyDescription, setCompetencyDescription] = React.useState<string>('');
  const [useManualInput, setUseManualInput] = React.useState(true);
  const [fetchingCourseDetails, setFetchingCourseDetails] = React.useState(false);

  const { data: competencyData, isLoading: isLoadingCompetency } = useQuery({
    queryKey: ['coreCompetency', competencyId],
    queryFn: () => coreCompetencyService.getById(competencyId),
    enabled: !!competencyId
  });

  const { data: jobProfilesData } = useQuery({
    queryKey: ['jobProfiles', businessUnitId, jobFamilyId, competencyData?.managementLevel],
    queryFn: async () => {
      const response = await courseService.getLCMSJobProfiles({
        bu: businessUnitId === 'All' ? '' : businessUnitId,
        jobFamilyId: jobFamilyId === 'All' ? '' : jobFamilyId,
        managementLevel:
          competencyData?.managementLevel === 'All' ? '' : competencyData?.managementLevel || '',
        pageNumber: 1,
        pageSize: 100,
        search: ''
      });
      return response;
    },
    enabled: !!competencyData
  });

  // Determine if we should show competency name dropdown
  const shouldShowCompetencyDropdown = React.useMemo(() => {
    const lowerType = competencyData?.competencyType?.toLowerCase() || '';
    return lowerType.includes('core') || lowerType.includes('leader') || lowerType.includes('fm');
  }, [competencyData?.competencyType]);

  // Determine the category for API call based on competency type
  const competencyCategory = React.useMemo(() => {
    const lowerType = competencyData?.competencyType?.toLowerCase() || '';
    if (lowerType.includes('leader')) return 'leader';
    if (lowerType.includes('core')) return 'core';
    if (lowerType.includes('fm')) return 'fm';
    return '';
  }, [competencyData?.competencyType]);

  // Query for fetching competency names
  const { data: competencyNamesData } = useQuery({
    queryKey: ['competencyNames', competencyCategory, competencyData?.managementLevel],
    queryFn: () =>
      coreCompetencyService.listCompetencyNames({
        category: competencyCategory,
        jobband: competencyData?.managementLevel || '',
        pageNumber: 1,
        pageSize: 100
      }),
    enabled:
      shouldShowCompetencyDropdown &&
      Boolean(competencyCategory) &&
      Boolean(competencyData?.managementLevel),
    staleTime: 5 * 60 * 1000
  });

  // Options are keyed by name: one position id can carry several different names (LPD data),
  // so the id is resolved from the name only when building the payload.
  const jobPositionOptions = React.useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const addedNames = new Set<string>();

    // Helper to add option without duplicates
    const addOption = (_id: string, name: string) => {
      if (!addedNames.has(name)) {
        options.push({ value: name, label: name });
        addedNames.add(name);
      }
    };

    // Priority 1: LPD positions
    if (lpdJobPositions.length > 0) {
      lpdJobPositions.forEach((pos) => addOption(pos.id, pos.name));
    }

    // Priority 2: Job profiles
    if (jobProfilesData?.data && jobProfilesData.data.length > 0) {
      jobProfilesData.data.forEach((item) => addOption(item.jobProfileId, item.jobProfileName));
    }

    // Priority 3: Competency data job positions (always include these)
    if (competencyData?.jobPositions && Array.isArray(competencyData.jobPositions)) {
      competencyData.jobPositions.forEach((pos) => addOption(pos.id, pos.name));
    }

    return options;
  }, [lpdJobPositions, jobProfilesData, competencyData?.jobPositions]);

  const getJobPositionIdByName = React.useCallback(
    (posName: string): string => {
      // Saved positions first, so saving never swaps a stored id for a same-named one.
      const compPos = competencyData?.jobPositions?.find((pos) => pos.name === posName);
      if (compPos) return compPos.id;

      const lpdPos = lpdJobPositions.find((pos) => pos.name === posName);
      if (lpdPos) return lpdPos.id;

      const jobProfile = jobProfilesData?.data?.find((pos) => pos.jobProfileName === posName);
      if (jobProfile) return jobProfile.jobProfileId;

      return posName;
    },
    [lpdJobPositions, jobProfilesData, competencyData?.jobPositions]
  );

  const form = useForm({
    initialValues: {
      jobPositions: [] as string[],
      competencyName: '',
      category: '',
      level: '1',
      categoryDescription: '',
      levelDescription: '',
      courses: [] as string[],
      schoolName: '',
      tahun: new Date().getFullYear().toString(),
      note: ''
    },
    validate: {
      jobPositions: (value: string[]) =>
        value.length === 0 ? 'At least one job position is required' : null,
      competencyName: (value: string) => (!value ? 'Competency name is required' : null),
      // Category and Level are no longer required for Core/Leader
      tahun: (value: string) => {
        // Year is only required for non-Core/Leader/FM competencies
        const lowerType = competencyData?.competencyType?.toLowerCase() || '';
        const isCoreOrLeaderOrFM =
          lowerType.includes('core') || lowerType.includes('leader') || lowerType.includes('fm');
        if (!isCoreOrLeaderOrFM && !value) {
          return 'Year is required';
        }
        return null;
      }
    },
    validateInputOnBlur: true,
    validateInputOnChange: ['jobPositions', 'competencyName']
  });

  // Custom validation for courses (checked on submit)
  const validateCourses = () => {
    if (selectedCourses.length === 0) {
      showErrorNotification({ message: 'At least one course is required' });
      return false;
    }
    return true;
  };

  React.useEffect(() => {
    if (competencyData) {
      // Parse jobPosition - handle both old format (single string) and new format (array of objects)
      let jobPositionArray: string[] = [];

      if (competencyData.jobPositions && Array.isArray(competencyData.jobPositions)) {
        // New format: array of {id, name} objects
        jobPositionArray = competencyData.jobPositions.map((pos: any) => pos.name);
      } else if (competencyData.jobPosition) {
        // Old format: single string
        jobPositionArray = [competencyData.jobPosition];
      }

      form.setValues({
        jobPositions: jobPositionArray,
        competencyName: competencyData.competencyName,
        category: competencyData.category,
        level: competencyData.level.toString(),
        categoryDescription: competencyData.categoryDescription || '',
        levelDescription: competencyData.levelDescription || '',
        courses: competencyData.courses.map((c) => c.id),
        schoolName: '', // Always empty
        tahun: competencyData.year?.toString() || new Date().getFullYear().toString(),
        note: competencyData.note || ''
      });

      // Restore competency description from saved data, fallback to navigation state
      const descFromApi = competencyData.competencyDescription;
      const descFromState = (location.state as any)?.competencyDescription;
      if (descFromApi) {
        setCompetencyDescription(descFromApi);
      } else if (descFromState) {
        setCompetencyDescription(descFromState);
      }

      // Map courses directly from API response (already has learningObjectives, learningHours, modeOfDelivery)
      const mappedCourses = competencyData.courses.map((course, index) => ({
        courseId: course.id,
        courseName: course.name,
        courseSequence: index + 1,
        courseCode: '',
        courseCategory: '',
        status: '',
        competencyCategory: '',
        scheduleStartDate: null as string | null,
        scheduleEndDate: null as string | null,
        createdAt: '',
        createdBy: '',
        checkInByName: '',
        checkInDateTime: null as string | null,
        checkOutByName: '',
        checkOutDateTime: null as string | null,
        isRequired: 1,
        positions: course.positions || [],
        learningObjectives: course.learningObjectives ?? undefined,
        learningHours: course.learningHours ?? undefined,
        modeOfDelivery: course.modeOfDelivery ?? undefined,
        liaIndicator: course.flaggingLIA ?? false
      }));
      setSelectedCourses(mappedCourses);
    }
  }, [competencyData]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => coreCompetencyService.update(competencyId, data),
    onSuccess: () => {
      showSuccessNotification({ message: 'Competency updated successfully' });
      navigate(-1);
    },
    onError: (error: any) => {
      showErrorNotification(error?.message || 'Failed to update competency');
    }
  });

  const handleAddCourses = React.useCallback(
    async (courses: any[]) => {
      if (!competencyData) return;
      setFetchingCourseDetails(true);

      const existingCourseIds = new Set(selectedCourses.map((course) => course.courseId));
      const newCourses = courses.filter((course) => !existingCourseIds.has(course.courseId));

      // Get current job position names from form
      const currentJobPositions = form.values.jobPositions;

      const coursesWithDetails = await Promise.all(
        newCourses.map(async (course) => {
          try {
            // Always use detail endpoint when manually adding courses
            const response = await courseService.detail(course.courseId);
            const detailData = response.data?.data;

            return {
              courseId: course.courseId,
              courseName: course.courseName,
              courseSequence: 0,
              courseCode: detailData?.basicInformation?.courseCode || '',
              courseCategory: detailData?.basicInformation?.courseCategory || '',
              status: detailData?.status || '',
              competencyCategory: detailData?.basicInformation?.competencyCategory || '',
              scheduleStartDate: null,
              scheduleEndDate: null,
              createdAt: '',
              createdBy: '',
              checkInByName: '',
              checkInDateTime: null,
              checkOutByName: '',
              checkOutDateTime: null,
              isRequired: 1,
              positions: currentJobPositions, // Auto-assign current job positions
              learningObjectives: detailData?.deliveryDetails?.learningObjectives,
              learningHours: detailData?.deliveryDetails?.learningHours,
              modeOfDelivery: detailData?.basicInformation?.modeOfDelivery,
              businessUnits: detailData?.basicInformation?.businessUnits,
              liaIndicator: detailData?.indicatorStandards?.liaIndicator
            };
          } catch {
            return {
              courseId: course.courseId,
              courseName: course.courseName,
              courseSequence: 0,
              courseCode: '',
              courseCategory: '',
              status: '',
              competencyCategory: '',
              scheduleStartDate: null,
              scheduleEndDate: null,
              createdAt: '',
              createdBy: '',
              checkInByName: '',
              checkInDateTime: null,
              checkOutByName: '',
              checkOutDateTime: null,
              isRequired: 1,
              positions: currentJobPositions, // Auto-assign current job positions
              learningObjectives: undefined,
              learningHours: undefined,
              modeOfDelivery: undefined,
              businessUnits: undefined,
              liaIndicator: undefined
            };
          }
        })
      );

      setSelectedCourses((prev) => {
        const existingIds = new Set(prev.map((item) => item.courseId));
        const nextCourses = [
          ...prev,
          ...coursesWithDetails
            .filter((course) => !existingIds.has(course.courseId))
            .map((course, index) => ({
              ...course,
              courseSequence: prev.length + index + 1
            }))
        ];
        return nextCourses;
      });
      setFetchingCourseDetails(false);
      closeCourseModal();
    },
    [selectedCourses, closeCourseModal, competencyData, form.values.jobPositions]
  );

  // Auto-sync positions when job positions change
  React.useEffect(() => {
    const currentJobPositions = form.values.jobPositions;

    // Update all courses with the new positions
    setSelectedCourses((prev) =>
      prev.map((course) => ({
        ...course,
        positions: currentJobPositions
      }))
    );
  }, [form.values.jobPositions]);

  const handleSubmit = (values: typeof form.values) => {
    if (!competencyData) return;

    // Validate courses before submitting
    if (!validateCourses()) {
      return;
    }

    // Map job position names back to their IDs
    const jobPositionsList = values.jobPositions.map((posName) => ({
      id: getJobPositionIdByName(posName),
      name: posName
    }));

    const payload = {
      // For Technical Competency, send names instead of IDs
      businessUnitId:
        competencyData.competencyType === 'Technical Competency'
          ? competencyData.businessUnitName
          : competencyData.businessUnitId,
      businessUnitName: competencyData.businessUnitName,
      jobFamilyId:
        competencyData.competencyType === 'Technical Competency'
          ? competencyData.jobFamilyName
          : competencyData.jobFamilyId,
      jobFamilyName: competencyData.jobFamilyName,
      managementLevel: competencyData.managementLevel,
      competencyType: competencyData.competencyType,
      schoolName: '',
      jobPositions: jobPositionsList,
      competencyName: values.competencyName,
      competencyDescription,
      msSchoolCompetencyManagementID:
        (competencyData as any)?.msSchoolCompetencyManagementID ||
        msSchoolCompetencyManagementID ||
        null,
      category: values.category,
      level: parseInt(values.level),
      categoryDescription: values.categoryDescription,
      levelDescription: values.levelDescription,
      note: values.note,
      year: parseInt(values.tahun),
      courses: selectedCourses.map((course) => ({
        courseId: course.courseId,
        courseName: course.courseName,
        positions: course.positions || []
      }))
    };

    updateMutation.mutate(payload);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const breadcrumbs = [
    { title: 'Competency Management', href: pathnames.competencyManagement },
    {
      title: 'Detail Competency',
      href: `${pathnames.detailCompetencyManagement}?${searchParams.toString()}`
    },
    { title: 'Edit Competency', href: '' }
  ];

  if (isLoadingCompetency) {
    return (
      <Container fluid>
        <Flex justify='center' align='center' h={400}>
          <Loader size='lg' />
        </Flex>
      </Container>
    );
  }

  if (!competencyData) {
    return (
      <Container fluid>
        <Paper p='xl'>
          <Text>Competency not found</Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container fluid>
      <BreadcrumbsComponent items={breadcrumbs} mb={32} />

      <Paper p='xl'>
        <Stack gap='xl'>
          <div>
            <Title order={3}>Edit Competency</Title>
            <Text size='sm' c='dimmed' mt='xs'>
              Update competency for {competencyData.businessUnitName} Business Unit -{' '}
              {competencyData.jobFamilyName} Job Family with {competencyData.managementLevel} Job
              Band.
            </Text>
          </div>

          {!msSchoolCompetencyManagementID && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              title='School Competency Not Mapped'
              color='orange'
              variant='light'
            >
              This competency does not have a school competency mapped. School competency mapping is{' '}
              <strong>required</strong> for master plan submission. Please map a school competency
              before proceeding.
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap='lg'>
              <MultiSelect
                label='Job Position'
                placeholder='Select job positions'
                data={(() => {
                  const selectedCount = form.values.jobPositions.length;
                  const options = [];

                  // Show "Select All" only when no job positions are selected
                  if (selectedCount === 0) {
                    options.push({ value: '__SELECT_ALL__', label: '✓ Select All Job Positions' });
                  }

                  // Show "Deselect All" only when more than 4 job positions are selected
                  if (selectedCount > 4) {
                    options.push({
                      value: '__DESELECT_ALL__',
                      label: '✕ Deselect All Job Positions'
                    });
                  }

                  return [...options, ...jobPositionOptions];
                })()}
                value={form.values.jobPositions}
                onChange={(values) => {
                  // Check if "Select All" was clicked
                  if (values.includes('__SELECT_ALL__')) {
                    const allPositions = jobPositionOptions.map((opt) => opt.value);
                    form.setFieldValue('jobPositions', allPositions);
                  } else if (values.includes('__DESELECT_ALL__')) {
                    // Check if "Deselect All" was clicked
                    form.setFieldValue('jobPositions', []);
                  } else {
                    form.setFieldValue('jobPositions', values);
                  }
                }}
                searchable
                required
                error={form.errors.jobPositions}
              />

              {shouldShowCompetencyDropdown ? (
                <Stack gap='xs'>
                  <Switch
                    label='Enter competency name manually'
                    description='Toggle this to type a custom competency name instead of selecting from the list'
                    checked={useManualInput}
                    onChange={(event) => {
                      setUseManualInput(event.currentTarget.checked);
                      if (event.currentTarget.checked) {
                        // Clear description when switching to manual
                        setCompetencyDescription('');
                      }
                    }}
                  />
                  {useManualInput ? (
                    <TextInput
                      label='Competency Name'
                      placeholder='Enter competency name'
                      {...form.getInputProps('competencyName')}
                      required
                    />
                  ) : (
                    <Select
                      label='Competency Name'
                      placeholder='Select competency name'
                      data={
                        competencyNamesData?.data?.map((item) => ({
                          value: item.competencyName,
                          label: item.competencyName ?? ''
                        })) || []
                      }
                      {...form.getInputProps('competencyName')}
                      onChange={(value) => {
                        form.setFieldValue('competencyName', value || '');
                        // Find and set the description
                        const selectedCompetency = competencyNamesData?.data?.find(
                          (item) => item.competencyName === value
                        );
                        setCompetencyDescription(selectedCompetency?.competencyDescription || '');
                      }}
                      searchable
                      required
                    />
                  )}
                </Stack>
              ) : (
                <TextInput
                  label='Competency Name'
                  placeholder='Enter competency name'
                  {...form.getInputProps('competencyName')}
                  required
                />
              )}

              <Textarea
                label='Competency Description'
                value={competencyDescription}
                onChange={(event) => setCompetencyDescription(event.currentTarget.value)}
                minRows={3}
                autosize
              />

              {/* For Technical Competency: Show category description textarea */}
              {competencyData?.competencyType === 'Technical Competency' ? (
                <Textarea
                  label='Category Description'
                  placeholder='Enter category description'
                  value={form.values.categoryDescription}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    form.setFieldValue('categoryDescription', value);
                    // Auto-set category based on categoryDescription for consistency
                    if (
                      value.toLowerCase().includes('high') ||
                      value.toLowerCase().includes('higher')
                    ) {
                      form.setFieldValue('category', 'Higher Order Thinking');
                    } else if (value.toLowerCase().includes('fundamental')) {
                      form.setFieldValue('category', 'Fundamental Thinking Skill');
                    } else {
                      if (!form.values.category) {
                        form.setFieldValue('category', 'Fundamental Thinking Skill');
                      }
                    }
                  }}
                  minRows={3}
                  autosize
                />
              ) : (
                <>
                  {/* For Core/Leader/FM: Show dropdown and textarea */}
                  <Select
                    label='Category'
                    placeholder='Select category'
                    data={[
                      { value: 'Higher Order Thinking', label: 'Higher Order Thinking' },
                      { value: 'Fundamental Thinking Skill', label: 'Fundamental Thinking Skill' }
                    ]}
                    value={form.values.category}
                    onChange={(value) => {
                      form.setFieldValue('category', value || '');
                      if (
                        value &&
                        CATEGORY_DESCRIPTIONS[value as keyof typeof CATEGORY_DESCRIPTIONS]
                      ) {
                        const currentDesc = form.values.categoryDescription;
                        const newPrefix =
                          CATEGORY_DESCRIPTIONS[value as keyof typeof CATEGORY_DESCRIPTIONS];
                        const oldPrefixes = Object.values(CATEGORY_DESCRIPTIONS);
                        let cleanDesc = currentDesc;
                        oldPrefixes.forEach((prefix) => {
                          if (cleanDesc.startsWith(prefix)) {
                            cleanDesc = cleanDesc.substring(prefix.length);
                          }
                        });
                        form.setFieldValue('categoryDescription', newPrefix + cleanDesc);
                      }
                    }}
                    error={form.errors.category}
                  />

                  <Textarea
                    label='Category Description'
                    placeholder='Enter category description'
                    {...form.getInputProps('categoryDescription')}
                    minRows={3}
                    autosize
                  />
                </>
              )}

              <Select
                label='Level'
                placeholder='Select level'
                data={[
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                  { value: '4', label: '4' },
                  { value: '5', label: '5' }
                ]}
                {...form.getInputProps('level')}
              />

              <Textarea
                label='Level Description'
                placeholder='Enter level description'
                {...form.getInputProps('levelDescription')}
                minRows={3}
                autosize
              />

              {!shouldShowCompetencyDropdown && (
                <Select
                  label='Year'
                  placeholder='Select year'
                  data={(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    for (let i = currentYear - 2; i <= currentYear + 5; i++) {
                      years.push({ value: i.toString(), label: i.toString() });
                    }
                    return years;
                  })()}
                  {...form.getInputProps('tahun')}
                  required
                />
              )}

              <Textarea
                label='Note'
                placeholder='Enter note'
                {...form.getInputProps('note')}
                minRows={3}
                autosize
              />

              <div>
                <Group justify='space-between' mb='md'>
                  <Text fw={600}>Selected Courses</Text>
                  {selectedCourses.length === 0 && (
                    <Button
                      leftSection={<IconPlus size={16} />}
                      onClick={openCourseModal}
                      variant='light'
                    >
                      Select Courses
                    </Button>
                  )}
                </Group>

                {selectedCourses.length === 0 ? (
                  <Paper p='md' withBorder>
                    <Text size='sm' c='dimmed' ta='center'>
                      No courses selected yet. Click "Select Courses" to add courses.
                    </Text>
                  </Paper>
                ) : (
                  <>
                    <ScrollArea>
                      <Table striped withTableBorder highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 60 }}>#</Table.Th>
                            <Table.Th>Course Name</Table.Th>
                            <Table.Th>Learning Objectives</Table.Th>
                            <Table.Th>Competency</Table.Th>
                            <Table.Th>Learning Hours</Table.Th>
                            <Table.Th>Mode of Delivery</Table.Th>
                            <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {selectedCourses.map((row, index) => (
                            <Table.Tr key={row.courseId}>
                              <Table.Td>
                                <Text fw={600}>{index + 1}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Stack gap={2} align='flex-start'>
                                  <Anchor
                                    href={`/app/lcms/courses/${row.courseId}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    size='md'
                                    fw={600}
                                  >
                                    {row.courseName}
                                  </Anchor>
                                </Stack>
                              </Table.Td>
                              <Table.Td style={{ maxWidth: 200 }}>
                                <Text size='sm' lineClamp={2}>
                                  {row.learningObjectives
                                    ? decodeHtml(row.learningObjectives)
                                    : '-'}
                                </Text>
                              </Table.Td>
                              <Table.Td>{row.competencyCategory || '-'}</Table.Td>
                              <Table.Td>{row.learningHours || '-'}</Table.Td>
                              <Table.Td>{row.modeOfDelivery || '-'}</Table.Td>

                              <Table.Td>
                                <Group justify='flex-end' gap={4}>
                                  <Tooltip label='View Course Details'>
                                    <ActionIcon
                                      variant='light'
                                      color='blue'
                                      size='sm'
                                      component='a'
                                      href={`/app/lcms/courses/${row.courseId}`}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      <IconEye size={16} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <ActionIcon
                                    variant='light'
                                    color='red'
                                    size='sm'
                                    onClick={() =>
                                      setSelectedCourses((prev) =>
                                        prev.filter((c) => c.courseId !== row.courseId)
                                      )
                                    }
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                    <Group justify='center' mt='md'>
                      <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={openCourseModal}
                        variant='light'
                        size='sm'
                      >
                        Add More Courses
                      </Button>
                    </Group>
                  </>
                )}
              </div>

              <Group justify='flex-end' mt='xl'>
                <Button variant='default' onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type='submit' loading={updateMutation.isPending}>
                  Update Competency
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>

      <CourseSelectionModal
        opened={courseModalOpened}
        onClose={closeCourseModal}
        onSubmit={handleAddCourses}
        selectedCourses={selectedCourses}
        competencyType={competencyData?.competencyType || ''}
        jobPositions={form.values.jobPositions}
        fetchingCourseDetails={fetchingCourseDetails}
      />
    </Container>
  );
};

interface CourseSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_courses: CourseSelectionPayload[]) => void;
  selectedCourses: SelectedCourse[];
  competencyType: string;
  jobPositions: string[];
  fetchingCourseDetails: boolean;
}

const CourseSelectionModal: React.FC<CourseSelectionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  selectedCourses,
  competencyType,
  jobPositions,
  fetchingCourseDetails
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selection, setSelection] = React.useState<Record<string, CourseOption>>({});

  React.useEffect(() => {
    if (opened) {
      const initialSelection: Record<string, CourseOption> = {};
      selectedCourses.forEach((course) => {
        initialSelection[course.courseId] = {
          msLCMSCourseID: course.courseId,
          courseName: course.courseName,
          courseCode: course.courseCode,
          courseCategory: course.courseCategory,
          status: course.status,
          competencyCategory: course.competencyCategory,
          scheduleStartDate: course.scheduleStartDate,
          scheduleEndDate: course.scheduleEndDate,
          createdAt: course.createdAt,
          createdBy: course.createdBy,
          checkInByName: course.checkInByName,
          checkInDateTime: course.checkInDateTime,
          checkOutByName: course.checkOutByName,
          checkOutDateTime: course.checkOutDateTime
        };
      });
      setSelection(initialSelection);
    }
  }, [opened, selectedCourses]);

  const { data, isFetching } = useQuery({
    queryKey: ['lcms-courses-selector', searchKeyword, page, competencyType, jobPositions],
    queryFn: () =>
      courseService.list({
        search: searchKeyword,
        pageNumber: page,
        pageSize: 10,
        // Pass job positions for all competency types
        ...(jobPositions.length > 0 ? { jobPosition: jobPositions.join(',') } : {})
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  const courses: CourseOption[] = (data?.data || []).map((item: any) => ({
    msLCMSCourseID: item.msLCMSCourseID || item.courseId || item.id,
    courseName: item.courseName || item.name || '-',
    courseCode: item.courseCode || item.code,
    courseCategory: item.courseCategory || item.category,
    status: item.status,
    competencyCategory: item.competencyCategory,
    scheduleStartDate: item.schedule?.startDate ?? null,
    scheduleEndDate: item.schedule?.endDate ?? null,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    checkInByName: item.checkInBy?.name,
    checkInDateTime: item.checkInBy?.checkInDateTime,
    checkOutByName: item.checkOutBy?.name,
    checkOutDateTime: item.checkOutBy?.checkOutDateTime
  }));
  const totalPages = data?.totalPages || 1;

  const toggleSelection = (course: CourseOption) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (next[course.msLCMSCourseID]) {
        delete next[course.msLCMSCourseID];
      } else {
        next[course.msLCMSCourseID] = course;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedList = Object.values(selection).map((item) => ({
      courseId: item.msLCMSCourseID,
      courseName: item.courseName,
      courseCode: item.courseCode,
      courseCategory: item.courseCategory,
      status: item.status,
      competencyCategory: item.competencyCategory,
      scheduleStartDate: item.scheduleStartDate,
      scheduleEndDate: item.scheduleEndDate,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
      checkInByName: item.checkInByName,
      checkInDateTime: item.checkInDateTime,
      checkOutByName: item.checkOutByName,
      checkOutDateTime: item.checkOutDateTime
    }));
    onSubmit(selectedList);
  };

  const selectedCount = Object.keys(selection).length;

  return (
    <Modal opened={opened} onClose={onClose} centered size='80%' radius='lg' shadow='lg'>
      <Stack gap='lg'>
        <Group justify='space-between' align='flex-start'>
          <Stack gap={4}>
            <Text fw={700} size='lg'>
              Select Courses
            </Text>
            <Text size='sm' c='dimmed'>
              Choose one or more courses to include in this competency.
            </Text>
          </Stack>
          <Text fw={600} c='blue'>
            Selected: {selectedCount}
          </Text>
        </Group>

        <TextInput
          placeholder='Search courses by name, code, or category'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
          size='md'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={120}>
            <Loader size='sm' />
          </Flex>
        ) : courses.length === 0 ? (
          <Stack align='center' gap='md' py='xl'>
            <Text size='sm' c='dimmed' ta='center'>
              No courses found.
            </Text>
            <Button
              variant='light'
              onClick={() => {
                window.open('/app/lcms/courses/add', '_blank');
              }}
            >
              Add New Course
            </Button>
          </Stack>
        ) : (
          <ScrollArea h={320} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Course</Table.Th>
                  <Table.Th>Course Code</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Competency Category</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Check In</Table.Th>
                  <Table.Th>Check Out</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {courses.map((course) => {
                  const checked = Boolean(selection[course.msLCMSCourseID]);
                  return (
                    <Table.Tr key={course.msLCMSCourseID}>
                      <Table.Td>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelection(course)}
                          aria-label={`Select ${course.courseName}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2} align='flex-start'>
                          <Text fw={600} size='md'>
                            {course.courseName}
                          </Text>
                          <Text size='xs' c='dimmed'>
                            {course.msLCMSCourseID}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>{course.courseCode || '-'}</Table.Td>
                      <Table.Td>{course.courseCategory || '-'}</Table.Td>
                      <Table.Td>{course.status || '-'}</Table.Td>
                      <Table.Td>{course.competencyCategory || '-'}</Table.Td>
                      <Table.Td>
                        <Stack gap={2} align='flex-start'>
                          <Text size='sm'>{course.createdBy || '-'}</Text>
                          <Text size='xs' c='dimmed'>
                            {formatDateTime(course.createdAt)}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatCheckInfo(course.checkInByName, course.checkInDateTime)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatCheckInfo(course.checkOutByName, course.checkOutDateTime)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose} disabled={fetchingCourseDetails}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={Object.keys(selection).length === 0}
            loading={fetchingCourseDetails}
          >
            Add Courses
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditCoreCompetencyPage;
